import _ from "lodash";
import React, { useContext, useState } from "react";
import moment from "moment";
import { useSearchParams } from "react-router-dom";
import { DocumentTypeFilter } from "../types/DocumentType";
import { CloseLoopFilter } from "../components/modals/close-loop-filter/types";

export type FilterContextProps = {
    clearFilter: (e: any, filters: any) => void,
    defaultFilters: any,
    filter: any,
    isFiltersApplied: boolean,
    isReady: boolean,
    setFilter: (prev: any) => void,
    setNewFilter: (data: { newFilter: any }) => void,
    showFilterModal: boolean,
    setShowFilterModal: (prev: boolean) => void
};

const FilterContext = React.createContext<FilterContextProps>({ 
    clearFilter: () => { },
    defaultFilters: { },
    filter: { },
    isFiltersApplied: false,
    isReady: false,
    setFilter: () => { },
    setNewFilter: () => { },
    showFilterModal: false,
    setShowFilterModal: () => { }
});

export const useFilterContext = () => useContext(FilterContext);

export const convertSearchParamsToFilter = (searchParams : URLSearchParams) : any => {
    let obj = { };
    for (const key of searchParams.keys()) {
        const entries = searchParams.getAll(key);
        obj[key] = entries.length === 1 ? entries[0] : entries;
    }
    return obj;
}

export const FilterProvider = ({ 
    children, 
    filterStore = null, 
    defaultFilters = {} 
} : Readonly<{ children: React.ReactNode, filterStore?: string | null, defaultFilters: any }>) : React.ReactNode => {
    const [filter, setFilter] = useState<any>(defaultFilters);                      // The current state of the filters
    const [hasFilteredOnSearch, setHasFilteredOnSearch] = useState<boolean>(false); // Checks if we have already applied the filters and basically don't do it again when we navigate back
    const [isReady, setIsReady] = useState<boolean>(false);                         // Allows the calling component to wait for session storage to be read before fetching with the filters
    const [showFilterModal, setShowFilterModal] = useState<boolean>(false);         // Allows showing and controlling of the filter modal. TODO: change this to new confirmable approach
    const [searchParams, setSearchParams] = useSearchParams();                      // Query Parameters to check for our dynamic filters to apply

    const changeFilter = () : void => {
        setIsReady(_ => false); // might as-well reset this..

        // try and pull the value out of session storage if we have applied the "store"
        const sessionFiltersString = filterStore ? sessionStorage.getItem(`filters.${filterStore}`) : null;
        const sessionFilters = sessionFiltersString != null ? JSON.parse(sessionFiltersString) : defaultFilters;

        // check if we have parameter filters that we want to override the defaults with
        const searchParamFilters = !hasFilteredOnSearch && searchParams && searchParams.size > 0 ? convertSearchParamsToFilter(searchParams) : {};

        // update state and update the ready status
        setFilter(() => ({ ...sessionFilters, ...searchParamFilters }));
        setIsReady(_ => true);
        setHasFilteredOnSearch(_ => true);
    };

    /**
     * Clear the filters back to either the default or a specific set of filters
     */
    const clearFilter = (e: any, filters: any | null = null) : void => {
        if (e && typeof e.preventDefault === "function") {
            e.preventDefault();
        }

        if (filterStore && sessionStorage.getItem(`filters.${filterStore}`)) {
            sessionStorage.removeItem(`filters.${filterStore}`);
        }

        setFilter(() => filters ?? defaultFilters);
    };

    /**
     * Updates the current filter with a new set of filters that need to be applied.
     * @param {*} param0 
     */
    const setNewFilter = ({ newFilter }: any) : void => {
        setFilter(() => newFilter);
    }

    /** Updates the filter store in the session storage whenever it gets updated.  */
    React.useEffect(() => {
        if (!filterStore || _.isEqual(filter, defaultFilters)) {
            return;
        }
        sessionStorage.setItem(`filters.${filterStore}`, JSON.stringify(filter));
    }, [filter]);

    /**
     * Run on component/content/provider mount. Will decide what to set the filters
     */
    React.useEffect(changeFilter, [ defaultFilters, searchParams ]);

    return (
        <FilterContext.Provider
            value={{
                clearFilter,
                defaultFilters,
                filter,
                isFiltersApplied: !_.isEqual(filter, defaultFilters),
                isReady,
                setFilter,
                setNewFilter,
                showFilterModal,
                setShowFilterModal
            }}
        >
            {children}
        </FilterContext.Provider>
    );
};

export const AssetManagementDefaultFilters = {
    assetName: null,
    sedol: null,
    provider: null,
    citicode: null
};


export const ReportManagementDefaultFilters = {
    reportStatuses: [],
    efficacy: [],
    masterAccountIds: [],
    masterAccounts: [],
    reportTypes: [],
    reportCategories: [],
    reportProductAreas: [],
    serviceTermIds: [],
    adviserIds: [],
    authorUserIds: [],
    complianceUserIds: [],
    mediaTypes: [],
    authorAssignedStartDate: null,
    authorAssignedEndDate: null,
    passedComplianceStartDate: null,
    passedComplianceEndDate: null,
    sentToClientStartDate: null,
    sentToClientEndDate: null,
    reportFeedbackFilters: [],
    searchTerm: "",
    excludeArchived: true,
    excludePrds: false
};

//Filter reviews from Jan 1st of the current year to the last day of the current month

export const ReviewManagementDefaultFilters = {
    reviewStatuses: [0, 3, 4],
    reviewDateStart: moment().startOf('month').format('YYYY/MM/DD'),
    reviewDateEnd: moment().endOf('month').format('YYYY/MM/DD'),
    serviceTermIds: [],
    adviserIds: [],
    reviewAdviserIds: [],
    completedTasks: [],
    overdueTasks: [],
    searchTerm: "",
    includeDelayed: true,
    excludeDelayed: false,
    onlyDelayed: false,
    completedTaskStandard: [],
    targetRatesMinimumStandard: 1,
    taskDueType: 0,    
};

export const CompletedReviewsDefaultFilters = {
    reviewDateStart: moment().startOf('month').format('YYYY/MM/DD'),
    reviewDateEnd: moment().endOf('month').format('YYYY/MM/DD'),
    serviceTermIds: [],
    adviserIds: [],
    searchTerm: "",
    includeDelayed: true,
    excludeDelayed: false,
    onlyDelayed: false,
    completedTaskStandard: []
};

export const ServiceTermsManagementDefaultFilters = {
    adviserIds: [],
    masterAccountIds: [],
    masterAccountStatusIds: [],
    serviceTermIds: [],
    serviceTermStatusIds: [],
    consentPublishedDateStart: null,
    consentPublishedDateEnd: null,
    consentSignedDateStart: null,
    consentSignedDateEnd: null,
    isNotPublished: false,
    isNotSigned: false
};

export const AdministrationDashboardDefaultFilters = {
    reference: null,
    accountName: null,
    statuses: [],
    startDate: null,
    endDate: null,
    organisationId: null,
    divisionId: null,
    adviserIds: [],
    campaignMembershipStatus: 1
};

export const AdministrationRemindersDefaultFilters = {
    message: null,
    createdByIds: [],
    reminderDateStart: null,
    reminderDateEnd: null,
    hideCompleted: true,
    completedByIds: [],
    completedDateStart: null,
    completedDateEnd: null
};

export const AdvicePendingDefaultFilters = {
    reference: null,
    accountName: null,
    statuses: [0,1,2,6],
    startDate: null,
    endDate: null,
    organisationId: null,
    divisionId: null,
    adviserIds: [],
    filterModel: [],
    campaignMembershipStatus: 1
};

export const CorporateActionPairFilters = {
    description: null,
    sedolIn: null,
    sedolOut: null,
    startDate: null,
    endDate: null
};

export const FeeManagementDefaultFilters = {
    organisationId: null,
    divisionId: null,
    adviserIds: [],
    advisers: [],
    introducerIds: [],
    introducers: [],
    providerIds: [],
    providers: [],
    feeTypes: [],
    masterAccountIds: [],
    masterAccounts: [],
    //madeUpThings: [],
    startDate: moment().startOf('month').format('YYYY/MM/DD'),
    endDate: null,
    debtOnDate: moment().format('YYYY/MM/DD')//for Debtors
    //debtOnDate: moment().toISOString()//for Debtors
};

{/*export const DebtorManagementDefaultFilters = {
    //organisationId: null,
    //divisionId: null,
    adviserIds: [],
    advisers: [],
    //introducerIds: [],
    //introducers: [],
    providerIds: [],
    providers: [],
    feeTypes: [],
    masterAccountIds: [],
    masterAccounts: [],
    //startDate: moment().startOf('month').toISOString(),
    //endDate: null,
    debtOnDate: moment().format('YYYY/MM/DD')//for Debtors

};*/}

export const AgedDebtorManagementDefaultFilters = {
    fromDate: moment().subtract(13, 'months').startOf('month').format("YYYY-MM-DD"),
    toDate: moment().subtract(1, 'months').endOf('month').format("YYYY-MM-DD"),
    feeTypes: []
};


export const IntroducerDashboardDefaultFilters = {
    introducer: "",
    typeIds: [],
    statusesIds: [],
    divisionIds: [],
    teamIds: [],
    adviserIds: []
};

export const InvestmentCommitteeFundDefaultFilters = {
    // Shared between Analysis/Management
    assetName: null,
    monitoring: [0],
    standardPortfolio: [],
    // Analysis only
    volatilityProfile: [],
    assetClass: [],
    management: [],
    region: [],
    style: [],
    // Management only
    controlStatus: [],
    opinion: [],
    guidance: [],
    analystId: [],
    publishedStartDate: null,
    publishedEndDate: null,
    opinionModifiedStartDate: null,
    opinionModifiedEndDate: null,
    publishedById: [],
};

export const InvestmentTransactionDefaultFilters = {
    designationIds: [],
    investmentOperationIds: [],
    providerIds: [],
    wrapperIds: [],
    productIds: [],
    assetIds: [],
    assetIdObjects: [],
    investmentPartnerIds: [],
    statusIds: [],
    custodianReference: null,
    providerReference: null,
    quantity: null,
    value: null,
    dateFrom: null,
    dateTo: null,
    keyTransactionsOnly: true,
    minimumAmount: 0,
    includeInValuation: true,
    isActive: true
};

export const ManagementReportsLeagueTableDefaultFilters = {
    //organisationId: null,
    //divisionId: null,
    adviserIds: [],
    advisers: [],
    reportDate: moment().endOf('month').format("YYYY-MM-DD"),
    //reportDate: moment().format("YYYY-MM-DD")
};

export const ProviderDefaultFilters = {
    custodianCode: null,
    fundManagerCode: null,
    providerName: null
};

export const NewMoneyDefaultFilters = {
    orgId: null,
    // divId: null,
    adviserIds: [],
    statuses: [],
    isOutflow: null,
    startDate: null,
    endDate: null,
}

export const ProjectManagementDefaultFilters = {
    divId: null,
    adviserIds: [],
    masterAccountIds: [],
    targetStartDate: moment().startOf('month').format("YYYY-MM-DD"),
    targetEndDate: moment().endOf('month').format("YYYY-MM-DD"),
    createdStartDate: null,
    createdEndDate: null,
    campaignId: null,
    campaignStatusIds: [],
    projectName: null,
    projectStatuses: [0], // Default to In Progress only
    adviceTypes: [],
    comments: null,
    hideInvisibleUsers: false
};

export const ApplicationDocumentsDefaultFilters = {
    fileName: null,
    userId: null,
    documentType: null,
    uploadedDateFrom: null,
    uploadedDateTo: null
};

export const UserDelegateSearchDefaultFilters = {
    userIds: [],
    delegateUserIds: [],
    createdByIds: [],
    readCalendar: false,
    writeCalendar: false,
    readMail: false,
    writeMail: false,
    sendMail: false,
    readMessages: false,
    writeMessages: false,
    readReminders: false,
    writeReminders: false,
    hasClientAccess: false
};

export const UsersGridDefaultFilters = { };

export const ClientEventsDefaultFilters = {
    showCancelled: false,
    projectId: null,
    userIds: [],
    deadlineDateFrom: null,
    deadlineDateTo: null,
    description: null,
    priorities: [],
    typeIds: []
};

export const PortfolioTestResultDefaultFilters = {
    masterAccountIds: [],
    serviceLevelAgreementIds: [],
    noServiceTerms: false,
    minPortfolioValue: null,
    maxPortfolioValue: null,
    targetProfileIds: [],
    reviewClientsOnly: false,
    volatilityScoreStatus: null,
    actualProfileIds: [],
    failedVolatilityTest: false,
    includeSlightFailVolatilityTest: false,
    flaggedStatuses: [],
    excludeFlagged: false,
    excludeUnflagged: false
}

export const AssetsUnderAdviceDefaultFilters = {
    divisionId: null,
    adviserIds: [],
    minPortfolioValue: null,
    maxPortfolioValue: null,
    serviceLevelAgreementIds: [],
    targetProfileIds: [],
    standardPortfolioIds: [],
    wrapperIds: [],
    productProviders: [],
    productProviderIds: [],
    productIds: [],
    assetGroupIds: [],
    assets: [],
    assetIds: [],
    minHoldingValue: null,
    maxHoldingValue: null,
    monitoring: [],
    assetClass: [],
    excludeShares: true,
    excludeDiscretionary: true,
    custodians: [],
    custodianIds: []
};

export const PersonalReminderDefaultFilters = {
    masterAccountId: null,
    project: null,
    showCancelled: false,
    userIds: [],
    users: [],
    deadlineDateTo: null,
    deadlineDateFrom: null,
    description: null,
    priorities: null,
    types: null
}

export const ClientDocumentDefaultFilters : DocumentTypeFilter = {
    documentTypes: [],
    documentFunctions: [],
    uploadedByUserIds: [],
    projectIds: [],
    clientReadable: null,
    dateUploadedFrom: null,
    dateUploadedTo: null,
    masterAccountIds: [],
    hideReportDocs: true
};

export const AdviceRegisterDefaultFilters = {
    masterAccountIds: [],
    sentToClientStartDate: moment().startOf('year').format("YYYY-MM-DD"),
    sentToClientEndDate: null,
    passedComplianceStartDate: null,
    passedComplianceEndDate: null,
    reportTypeIds: [],
    authorIds: [],
    adviserIds: [],
    complianceOfficerIds: [],
    minRejectCount: null,
    maxRejectCount: null,
    reportStatusIds: [],
    businessSheetStatuses: [],
    vulnerableOnly: false,
    replacementOnly: false,
    reviewStartDate: null,
    reviewEndDate: null,
    serviceLevelAgreementIds: []
}

export const ClientApplicationsDefaultFilters = {
    reference: null,
    accountName: null,
    statuses: [],
    startDate: null,
    endDate: null,
    organisationId: null,
    divisionId: null,
    adviserIds: [],
    searchTerm: null
}

export const NewBusinessRegisterDefaultFilters = {
    // Default is from this month of previous year to end of previous month
    startDate: moment.utc().startOf('month').subtract(1, 'year').format("YYYY-MM-DD"),
    endDate: moment.utc().endOf('month').subtract(1, 'month').format("YYYY-MM-DD"),
    applicationReferenceId: "",
    applicationDateFrom: null,
    applicationDateTo: null,
    minInvestment: null,
    maxInvestment: null,
    minFee: null,
    maxFee: null,
    minNetFee: null,
    maxNetFee: null,
    masterAccountIds: [],
    adviserIds: [],
    adviceTypes: [],
    reportIds: [],
    executionOnly: false,
    serviceLevelAgreementIds: []
}

export const CustodianCashBalancesDefaultFilters = {
    workingDate : moment().format('YYYY/MM/DD'),
    adviserIds: [],
    minimumBalance: 20000.00,
    masterAccountIds: [],

}

export const CloseLoopDashboardDefaultFilters: CloseLoopFilter = {
    hasLogin: false,
    masterAccountIds: [],
    isAjBellAccount: false,
    applicationDate: { from: null, to: null },
    lastDealDate: { from: null, to: null },
    statuses: [],
    statusDate: { from: null, to: null },
    providers: []
};

export const HubwiseJobDefaultFilters = {
    organisationIds: [],
    startedByIds: [],
    runDateFrom: null,
    runDateTo: null,
    statuses: []
};